// components/legal/terms.jsx
"use client";

import React, { useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";
import { getLegalProductContent } from "@/i18n/legalProductContent";

export default function TermsOfService() {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "legal"), [locale]);
  const tc = useMemo(() => getStrings(locale, "copilot_product"), [locale]);
  const productLegal = useMemo(() => getLegalProductContent(locale).terms, [locale]);

  return (
    <article className="break-words text-base leading-7 text-slate-700">
      <section className="pt-8 pb-20 sm:pt-10 sm:pb-24">
        <div className="max-w-4xl">
          <header className="mb-12">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600">{t.legal_docs_label}</p>
            <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-6xl">{t.terms_title}</h1>
            <p className="mt-5 text-sm text-slate-500">
              {t.last_updated}: <span className="font-semibold">{t.terms_last_updated_date}</span>
            </p>
            <p className="mt-6 text-base leading-8 text-slate-600">{t.terms_intro}</p>
            <p className="mt-3 text-sm leading-6 text-slate-500">{t.terms_disclaimer}</p>
          </header>

          <section className="mt-10 border-t border-slate-200 pt-8" id="current-products">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{productLegal.title}</h2>
            <p className="mt-3 text-slate-700">{productLegal.intro}</p>
            <ul className="mt-4 list-disc space-y-2 pl-6 text-slate-700">
              {productLegal.products.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <h3 className="mt-6 font-semibold text-slate-900">{productLegal.boundariesTitle}</h3>
            <ul className="mt-3 list-disc space-y-2 pl-6 text-slate-700">
              {productLegal.boundaries.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          {/* 1) Acceptance & Eligibility */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="acceptance">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s1_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s1_li1}</li>
              <li>{t.terms_s1_li2}</li>
              <li>{t.terms_s1_li3}</li>
            </ul>
          </section>

          {/* 2) Account, Gmail Requirement & Security */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="account">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s2_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s2_li1}</li>
              <li>{t.terms_s2_li2}</li>
              <li>{t.terms_s2_li3}</li>
              <li>{t.terms_s2_li4}</li>
            </ul>

            <h3 className="mt-4 font-semibold text-slate-900">{t.terms_s2_gmail_title}</h3>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s2_gmail_li1}</li>
              <li>{t.terms_s2_gmail_li2}</li>
              <li>{t.terms_s2_gmail_li3}</li>
              <li>{t.terms_s2_gmail_li4}</li>
            </ul>

            <h3 className="mt-4 font-semibold text-slate-900">{t.terms_s2_prohibited_title}</h3>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s2_prohibited_li1}</li>
              <li>{t.terms_s2_prohibited_li2}</li>
              <li>{t.terms_s2_prohibited_li3}</li>
              <li>{t.terms_s2_prohibited_li4}</li>
            </ul>
          </section>

          {/* 3) Description of Services */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="services">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s3_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s3_li1}</li>
              <li>{t.terms_s3_li2}</li>
              <li>{t.terms_s3_li3}</li>
              <li>{t.terms_s3_li4}</li>
            </ul>
          </section>

          {/* AI Interview Assistant */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="interview-copilot">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{tc.terms_copilot_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              {Array.from({ length: 8 }, (_, index) => (
                <li key={index}>{tc[`terms_copilot_li${index + 1}`]}</li>
              ))}
            </ul>
          </section>

          {/* 4) User Responsibilities & Acceptable Use */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="acceptable-use">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s4_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s4_li1}</li>
              <li>{t.terms_s4_li2}</li>
              <li>{t.terms_s4_li3}</li>
              <li>{t.terms_s4_li4}</li>
              <li>{t.terms_s4_li5}</li>
            </ul>
          </section>

          {/* 5) Subscriptions, Billing & Refunds */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="billing">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s5_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s5_intro}</p>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s5_li1}</li>
              <li>{t.terms_s5_li2}</li>
              <li>{t.terms_s5_li3}</li>
            </ul>
          </section>

          {/* 5A) Payment Processors */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="payment-processors">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s5a_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s5a_intro}</p>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s5a_stripe}</li>
              <li>{t.terms_s5a_flutterwave}</li>
            </ul>
            <p className="mt-3 text-slate-700">{t.terms_s5a_authorize}</p>
            <p className="mt-2 text-slate-700">{t.terms_s5a_no_store}</p>
            <p className="mt-2 text-slate-700">{t.terms_s5a_subject}</p>
          </section>

          {/* 6) Intellectual Property */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="ip">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s6_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s6_li1}</li>
              <li>{t.terms_s6_li2}</li>
            </ul>
          </section>

          {/* 7) User Content & License to JOBolar */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="user-content">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s7_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s7_li1}</li>
              <li>{t.terms_s7_li2}</li>
              <li>{t.terms_s7_li3}</li>
              <li>{t.terms_s7_li4}</li>
            </ul>
          </section>

          {/* 8) Deliverability, Limits & Third-Party Systems */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="deliverability">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s8_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s8_li1}</li>
              <li>{t.terms_s8_li2}</li>
              <li>{t.terms_s8_li3}</li>
            </ul>
          </section>

          {/* 9) Third-Party Links & Services */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="third-party">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s9_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s9_li1}</li>
              <li>{t.terms_s9_li2}</li>
            </ul>
          </section>

          {/* 10) Disclaimers */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="disclaimers">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s10_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s10_text}</p>
          </section>

          {/* 11) Limitation of Liability */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="liability">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s11_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s11_text}</p>
          </section>

          {/* 12) Indemnification */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="indemnification">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s12_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s12_text}</p>
          </section>

          {/* 13) Termination & Suspension */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="termination">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s13_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s13_li1}</li>
              <li>{t.terms_s13_li2}</li>
              <li>{t.terms_s13_li3}</li>
            </ul>
          </section>

          {/* 14) Governing Law & Dispute Resolution */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="law-disputes">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s14_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s14_text}</p>
          </section>

          {/* 15) Changes to the Services or Terms */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="changes">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s15_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s15_li1}</li>
              <li>{t.terms_s15_li2}</li>
            </ul>
          </section>

          {/* 16) Contact */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="contact">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.terms_s16_title}</h2>
            <p className="mt-2 text-slate-700">{t.terms_s16_intro}</p>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.terms_s16_email_label} <a href="mailto:legal@JOBolar.Ai" className="text-indigo-600 underline">legal@JOBolar.Ai</a></li>
              <li>
                <address className="not-italic">
                  {t.terms_s16_address_label}<br />
                  330 2nd Ave S 200 1319<br />
                  Minneapolis, MN 55401<br />
                  USA
                </address>
              </li>
            </ul>
            <p className="mt-2 text-slate-700">{t.terms_s16_support}</p>
          </section>
        </div>
      </section>
    </article>
  );
}
